import { getRoofRidges, getRoofValleys } from "building-common/roofs/roofQuery";
import { buildRoofToWallRails } from "building-generation/elements/roofTools/roofRails";
import { getRoofEaves, getRoofRakes, getRoofRidgeVents } from "building-generation/utils/roofKeymeasures";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { GeneratedRoof, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { faceArea, facesArea } from "geom/brep";
import { DIST_EPSILON } from "geom/core";
import { planeNormal } from "geom/plane";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3Length } from "geom/polyLine3";

function getRoofArea(roofTopFaces: RoofFace[]): number {
  let totalArea = 0;

  for (const roofTopFace of roofTopFaces) {
    totalArea += faceArea(roofTopFace.face);
  }

  return totalArea / 144;
}

function getExtrudedArea(extrudedFaces: FaceArrayViewEntity[]): number {
  let totalArea = 0;

  for (const extrudedFace of extrudedFaces) {
    const faces = extrudedFace.faces.filter((face) => {
      const [, , nZ] = planeNormal(face.plane);
      return nZ > DIST_EPSILON;
    });

    totalArea += facesArea(faces);
  }

  return totalArea / 144;
}

export function getRailsLength(rails: PolyLine3[]): number {
  let totalLength = 0;

  for (let i = 0, l = rails.length; i < l; i++) {
    totalLength += polyLine3Length(rails[i]);
  }

  return totalLength;
}

export interface TraversedRoofArea {
  groupName: string;
  area: number;
  exteriorMaterial: ExteriorMaterial;
  count: number;
  handles: DocEntityHandle[];
}

export interface TraversedRoofEave {
  name: string;
  length: number;
}

export interface TraversedRoofRidge {
  name: string;
  length: number;
  count?: number;
}

export interface TraversedRoofRidgeVent {
  name: string;
  length: number;
}

export interface TraversedRoofValley {
  name: string;
  length: number;
  count?: number;
}

export interface TraversedRoofFlashing {
  name: string;
  length: number;
}

function traverseSubRoofKeyMeasures(
  subRoof: GeneratedRoof,
  groupName: string,
  onTraversedRoofArea: (traversed: TraversedRoofArea) => void,
): void {
  const { extrudedTrimsRoofMat, roofTopFaces, roofSettings } = subRoof;
  const { roofingMaterial = "roof0", eaveReturnRoofMaterial } = roofSettings;

  const area = getRoofArea(roofTopFaces);
  const eaveReturnArea = getExtrudedArea(extrudedTrimsRoofMat);

  onTraversedRoofArea({
    groupName,
    area,
    exteriorMaterial: roofingMaterial,
    count: 1,
    handles: [buildingEntityPickHandle(subRoof)],
  });

  onTraversedRoofArea({
    groupName: "Eave Returns",
    area: eaveReturnArea,
    exteriorMaterial: eaveReturnRoofMaterial ?? roofingMaterial,
    count: 1,
    handles: extrudedTrimsRoofMat.map((eaveRoof) => eaveRoof.pickHandle).filter((h) => h !== undefined),
  });
}

export function traverseRoofMeasurements({
  roofs,
  walls,
  onTraversedRoofArea,
  onTraversedRoofEave,
  onTraversedRoofRidge,
  onTraversedRoofRidgeVent,
  onTraversedRoofValley,
  onTraversedRoofFlashing,
  filter,
}: {
  roofs: Table<GeneratedRoofSystem>;
  walls: Table<WallSystem>;
  onTraversedRoofArea: (traversed: TraversedRoofArea) => void;
  onTraversedRoofEave?: (traversed: TraversedRoofEave) => void;
  onTraversedRoofRidge?: (traversed: TraversedRoofRidge) => void;
  onTraversedRoofRidgeVent?: (traversed: TraversedRoofRidgeVent) => void;
  onTraversedRoofValley?: (traversed: TraversedRoofValley) => void;
  onTraversedRoofFlashing?: (traversed: TraversedRoofFlashing) => void;
  filter?: (entity: GeneratedRoofSystem | GeneratedRoof) => boolean;
}): void {
  for (const roofId in roofs) {
    const roof = roofs[roofId];

    // only supported for multi-gable roofs
    const { roofs: subRoofs } = roof;

    if (onTraversedRoofRidge !== undefined) {
      const horizontalRidges = getRoofRidges(roof, true, filter);
      const horizontalRidgeLength = getRailsLength(horizontalRidges);
      onTraversedRoofRidge({ name: "Horizontal", length: horizontalRidgeLength, count: horizontalRidges.length });

      const angledRidges = getRoofRidges(roof, false, filter);
      const angledRidgeLength = getRailsLength(angledRidges);
      onTraversedRoofRidge({ name: "Angled", length: angledRidgeLength, count: angledRidges.length });
    }

    if (onTraversedRoofRidgeVent !== undefined) {
      const ridgeVents = getRoofRidgeVents(roof, filter);
      const ridgeVentLength = getRailsLength(ridgeVents);
      onTraversedRoofRidgeVent({ name: "", length: ridgeVentLength });
    }

    if (onTraversedRoofValley !== undefined) {
      const valleyRails = getRoofValleys(roof, filter);
      const valleyLength = getRailsLength(valleyRails);
      onTraversedRoofValley({ name: "", length: valleyLength, count: valleyRails.length });
    }

    if (onTraversedRoofFlashing !== undefined) {
      // NOTE: buildRoofToWallRails does not support filtering by subroof. The rails are generated through
      // interactions between many subroofs, so it is unclear which subroof the rails belong to.
      const roofToWallRails = buildRoofToWallRails(roof, walls, true);
      const slopeToWallRails = buildRoofToWallRails(roof, walls, false);
      const flashingLength = getRailsLength(roofToWallRails);
      const slopeFlashingLength = getRailsLength(slopeToWallRails);
      onTraversedRoofFlashing({ name: "Horizontal", length: flashingLength });
      onTraversedRoofFlashing({ name: "Sloped", length: slopeFlashingLength });
    }

    if (onTraversedRoofEave !== undefined) {
      const eaveRails = getRoofEaves(roof, filter);
      const rakeRails = getRoofRakes(roof, filter);
      const eaveLength = getRailsLength(eaveRails);
      const rakeLength = getRailsLength(rakeRails);
      onTraversedRoofEave({ name: "Eaves", length: eaveLength });
      onTraversedRoofEave({ name: "Rakes", length: rakeLength });
    }

    for (const subRoofId in subRoofs) {
      const subRoof = subRoofs[subRoofId];
      if (filter && !filter(subRoof)) {
        continue;
      }
      traverseSubRoofKeyMeasures(subRoof, subRoof.isDormer ? "Dormers" : "Primary Roofs", onTraversedRoofArea);
    }
  }
}
